"use client"

import { For, Icon, SegmentGroup, useSlotRecipe } from "@chakra-ui/react"
import * as React from "react"
import segment from "./styling/segment"
import { IconType } from "react-icons"
import { Tooltip } from "./Tooltip"

interface Item {
  value: string
  label: React.ReactNode
  disabled?: boolean
  icon?: IconType
  /** Shown on hover, e.g. why the option is disabled */
  tooltip?: React.ReactNode
}

export interface SegmentedControlProps extends SegmentGroup.RootProps {
  items: Array<string | Item>
  variant?: "hydrologiq" | "hydrologiq_icon" | "hydrologiq_segmented"
}

function normalize(items: Array<string | Item>): Item[] {
  return items.map((item) => {
    if (typeof item === "string") return { value: item, label: item }
    return item
  })
}

export const SegmentedControl = React.forwardRef<HTMLDivElement, SegmentedControlProps>(function SegmentedControl(
  props,
  ref
) {
  const variant = props.variant ?? "hydrologiq"
  const recipe = useSlotRecipe({ recipe: segment })
  const styles = recipe({ variant })
  const { items, ...rest } = props
  const data = React.useMemo(() => normalize(items), [items])

  return (
    <SegmentGroup.Root ref={ref} {...rest} css={styles.root}>
      <For each={data}>
        {(item) => (
          <Tooltip
            key={item.value}
            content={item.tooltip}
            disabled={!item.tooltip}
            // Rendered in place, not at the body, so a Dialog holding the control can't cover it;
            // fixed so the root's overflow: hidden doesn't clip it
            portalled={false}
            positioning={{ strategy: "fixed" }}
          >
            <SegmentGroup.Item value={item.value} disabled={item.disabled} css={styles.item}>
              <SegmentGroup.ItemText>
                {item.icon ? <Icon as={item.icon} height={"100%"} width={"20px"} /> : item.label}
              </SegmentGroup.ItemText>
              <SegmentGroup.ItemHiddenInput />
            </SegmentGroup.Item>
          </Tooltip>
        )}
      </For>
    </SegmentGroup.Root>
  )
})
